// R115 A1 — the MTR rent ledger, extracted from MtrPage.showPaymentsModal so
// the Calendar's MTR view (tenant inspector / stay section) and the MTR →
// Tenants tab share ONE ledger.
// R116 #4 (Kai: "add option for notes so we can put proper documentation why
// payments are not logged yet and/or lease are not being extended … check by
// 15-17 for checks to clear") — the ledger grows into the TENANT PANEL, still
// one component for both surfaces, in the Calendar stay panel's look (soft
// cards, small uppercase section labels with count chips, ONE blue full-width
// primary, pills, muted helper text, the rest behind the shared ⋯):
//   • the primary: "✓ <month> check cleared" when a payment is clearing, else
//     "Log <owed month> payment" (it opens the log form, which then owns the
//     only blue button);
//   • a summary card: home, rent chip (OWES / CLEARING · by Oct 17 / current),
//     the open follow-up pill, lease / rent / next due, and for an AUTO tenant
//     "the lease follows the Hostaway booking — extend it there";
//   • NOTES n: a composer (optional "⏰ follow-up" window — "check Oct 15–17" —
//     and an optional month; default: no month), the notes newest first with
//     who · when · edited, the follow-up pill + "✓ Mark checked" (any office
//     role), ⋯ Edit / Delete (author or admin / director — the server's
//     can_edit), a two-tap delete;
//   • RENT n: owed months (no record yet — "Log") + every payment, newest
//     first: kind (rent / deposit / fee / other), clearing rows with a one-tap
//     "Cleared", ⋯ re-file the kind / delete (two-tap); the log form: month,
//     kind, amount, received, "check still clearing" + expected date, proof,
//     Memo (the payment's own label — "Oct rent, SD, CF…");
//   • HISTORY n (collapsed): the tenant's log.
//
//   mountRentLedger(host, opts)   → renders INTO a host the caller owns (the
//                                   Calendar's form sheet); returns { redraw, isDirty }
//   openRentLedgerModal(opts)     → the MTR page's modal wrapper (z 300 — the
//                                   sheet layer; kebabs 340 / toasts 400 stay above)
//   openMtrSheet(…)               → the same modal shell for other MTR sheets
//                                   (the tenant Edit / Add sheet)
//
// The caller owns the data: getTenant() answers the CURRENT tenant view;
// applyTenant(v) swaps a write's answer in place (no roster reload — a failed
// reload used to blank the roster); without it refresh() re-fetches. No alert /
// confirm (both block headless tests and phones' focus): errors show inline and
// every delete is two-tap. Rent $ here is the office's operational ledger
// (office roles — the Tenants tab's rule), never owner money.
// HostBuddy idiom: :root tokens only, --accent the one action colour.
import { kebabHtml, bindKebabs, closeKebab, type KebabItem } from './Kebab';
import {
  esc, money, md, fmtFull, periodLabel, windowLabel, localToday, addDays, addMonths, initials, safeUrl, followTone, followText, chipOf,
  whoFrom, ago, fmtWhen, PAY_KINDS,
} from '../lib/mtrTenant';

export interface RentLedgerOpts {
  tenantId: string;
  getTenant: () => any | null;
  refresh: () => Promise<void>;
  onChanged?: () => void;
  onClose?: () => void;
  /** R116: swap a write's answer (the tenant view) in place; without it refresh() runs after each write. */
  applyTenant?: (v: any) => void;
  /** R116: the panel holds typed text (a host vetoes its refresh / asks before discarding). */
  onDirty?: (dirty: boolean) => void;
  /** R116: email → display name (the people list); default = the email's local part. */
  whoName?: (by: string) => string;
  /** R116: open with the note composer focused ('note') or the payment form open ('pay'). */
  focus?: 'note' | 'pay';
}

const CSS_ID = 'mrl-css';
const CSS = `
.mrl { display:flex; flex-direction:column; gap:14px; font-size:var(--fs-body, 12px); color:var(--text); }
.mrl-primary { width:100%; justify-content:center; min-height:40px; font-size:13px; }
.mrl-card { border:1px solid var(--border); border-radius:var(--radius); background:var(--card); box-shadow:var(--shadow-card); padding:14px; }
.mrl-sum-top { display:flex; align-items:flex-start; gap:12px; }
.mrl-hav { flex:none; width:38px; height:38px; border-radius:var(--radius-sm); background:var(--accent-soft); display:inline-flex; align-items:center; justify-content:center; font-size:17px; }
.mrl-sum-txt { flex:1; min-width:0; }
.mrl-sum-home { font-size:13.5px; font-weight:600; color:var(--text); overflow-wrap:anywhere; }
.mrl-sum-sub { font-size:11.5px; color:var(--text-muted); margin-top:2px; }
.mrl-pills { display:flex; flex-wrap:wrap; gap:6px; margin-top:10px; }
.mrl-facts { display:grid; grid-template-columns:minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, .8fr); gap:10px; margin-top:12px; padding-top:12px; border-top:1px solid var(--border-soft); }
.mrl-dl { font-size:10.5px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:var(--text-faint); }
.mrl-dv { font-size:13px; font-weight:600; color:var(--text); margin-top:3px; font-variant-numeric:tabular-nums; overflow-wrap:anywhere; }
.mrl-dv small { font-weight:500; color:var(--text-muted); font-size:11.5px; }
.mrl-fine { font-size:11.5px; color:var(--text-faint); line-height:1.5; margin-top:10px; }
.mrl-fine b { color:var(--text-muted); font-weight:600; }
.mrl-pill { display:inline-flex; align-items:center; gap:4px; font-size:11px; font-weight:600; padding:3px 9px; border-radius:var(--radius-pill); white-space:nowrap; border:1px solid var(--border); background:var(--bg-subtle); color:var(--text-muted); line-height:1.4; max-width:100%; overflow:hidden; text-overflow:ellipsis; }
.mrl-pill.ok { background:var(--success-soft); color:var(--success-text); border-color:var(--success-border); }
.mrl-pill.warn { background:var(--warning-soft); color:var(--warning-text); border-color:var(--warning-border); }
.mrl-pill.bad { background:var(--danger-soft); color:var(--danger-text); border-color:var(--danger-border); }
.mrl-pill.info { background:var(--accent-soft); color:var(--accent); border-color:var(--accent-border); }
.mrl-pill.muted { background:var(--card); color:var(--text-muted); }
.mrl-pill.kind { text-transform:uppercase; letter-spacing:.05em; font-size:9.5px; padding:2px 7px; }
.mrl-sec { display:flex; align-items:center; gap:8px; margin:6px 0 -4px; font-size:11px; font-weight:700; letter-spacing:.09em; text-transform:uppercase; color:var(--text-muted); }
.mrl-sec .mrl-cnt { display:inline-flex; align-items:center; justify-content:center; min-width:20px; height:20px; padding:0 6px; border-radius:var(--radius-pill); background:var(--bg-subtle); border:1px solid var(--border); color:var(--text); font-size:11px; letter-spacing:0; font-variant-numeric:tabular-nums; }
.mrl-sec .mrl-cnt.warn { background:var(--warning-soft); color:var(--warning-text); border-color:var(--warning-border); }
.mrl-sec .mrl-cnt.bad { background:var(--danger-soft); color:var(--danger-text); border-color:var(--danger-border); }
.mrl-sec-r { margin-left:auto; display:flex; gap:6px; align-items:center; }
.mrl-compose { display:flex; flex-direction:column; gap:8px; }
.mrl-ta { display:block; width:100%; box-sizing:border-box; min-height:70px; resize:vertical; font:inherit; font-size:13px; line-height:1.5; padding:9px 11px; border:1px solid var(--border); border-radius:var(--radius-sm); background:var(--card); color:var(--text); }
.mrl-ta:focus, .mrl-in:focus, .mrl-sel:focus { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
.mrl-opts { display:flex; flex-wrap:wrap; gap:6px; align-items:center; }
.mrl-tog { display:inline-flex; align-items:center; gap:5px; min-height:32px; padding:0 11px; border-radius:var(--radius-pill); border:1px solid var(--border); background:var(--card); color:var(--text-muted); font:inherit; font-size:12px; font-weight:600; cursor:pointer; }
.mrl-tog:hover { border-color:var(--accent-border); color:var(--accent); }
.mrl-tog.on { background:var(--accent-soft); color:var(--accent); border-color:var(--accent-border); }
.mrl-fu { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:8px; padding:10px; border-radius:var(--radius-sm); background:var(--bg-subtle); border:1px solid var(--border-soft); }
.mrl-lab { display:flex; flex-direction:column; gap:4px; font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); min-width:0; }
.mrl-in, .mrl-sel { font:inherit; font-size:13px; text-transform:none; letter-spacing:0; font-weight:400; padding:7px 10px; min-height:36px; border:1px solid var(--border); border-radius:var(--radius-sm); background:var(--card); color:var(--text); box-sizing:border-box; width:100%; min-width:0; }
.mrl-in[type=file] { padding:6px; font-size:12px; }
.mrl-row-acts { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.mrl-row-acts .mrl-hint { flex:1; min-width:150px; }
.mrl-row-acts .btn { min-height:34px; }
.mrl-add:not(:disabled) { color:var(--accent); border-color:var(--accent-border); }
.mrl-hint { color:var(--text-faint); font-size:11px; line-height:1.45; }
.mrl-notes { display:flex; flex-direction:column; gap:8px; }
.mrl-note { border:1px solid var(--border); border-radius:var(--radius-sm); background:var(--card); padding:10px 12px; }
.mrl-note.fu-now { box-shadow:inset 3px 0 0 var(--warning); }
.mrl-note.fu-overdue { box-shadow:inset 3px 0 0 var(--danger); }
.mrl-note.editing { border-color:var(--accent-border); }
.mrl-note-h { display:flex; align-items:center; gap:6px; font-size:11.5px; color:var(--text-muted); min-height:26px; flex-wrap:wrap; }
.mrl-note-h .who { font-weight:600; color:var(--text); }
.mrl-note-h .who.anon { font-weight:500; font-style:italic; color:var(--text-faint); }
.mrl-note-h .kb-btn { margin-left:auto; color:var(--text-muted); }
.mrl-note-ctx { font-size:11px; color:var(--text-muted); margin-top:2px; }
.mrl-note-t { margin-top:4px; font-size:12.5px; line-height:1.5; color:var(--text); white-space:pre-wrap; overflow-wrap:anywhere; }
.mrl-note-f { display:flex; align-items:center; flex-wrap:wrap; gap:6px 8px; margin-top:8px; }
.mrl-note-f .btn { min-height:30px; }
.mrl-nudge { font-size:11.5px; color:var(--success-text); }
.mrl-arm { display:flex; align-items:center; flex-wrap:wrap; gap:6px 10px; margin-top:8px; padding:8px 10px; border-radius:var(--radius-sm); background:var(--danger-soft); color:var(--danger-text); font-size:12px; }
.mrl-arm .btn { min-height:32px; }
.mrl-arm span { flex:1; min-width:140px; }
.mrl-list { border:1px solid var(--border); border-radius:var(--radius); background:var(--card); box-shadow:var(--shadow-card); overflow:hidden; }
.mrl-row { display:grid; grid-template-columns:minmax(92px, .9fr) minmax(70px, .7fr) minmax(120px, 1.6fr) auto; gap:4px 10px; align-items:center; padding:10px 12px; border-bottom:1px solid var(--border-soft); }
.mrl-row:last-child { border-bottom:none; }
/* the rows share ONE set of columns (a subgrid), so months, amounts and receipts line up row to row */
@supports (grid-template-columns: subgrid) {
  .mrl-list { display:grid; grid-template-columns:minmax(110px, max-content) minmax(72px, max-content) minmax(0, 1fr) max-content; }
  .mrl-list > .mrl-row { grid-column:1 / -1; grid-template-columns:subgrid; column-gap:14px; }
  .mrl-list > .mrl-empty { grid-column:1 / -1; }
}
.mrl-amt { text-align:right; }
.mrl-row.owed { background:var(--danger-soft); }
.mrl-row.clearing { background:var(--warning-soft); }
.mrl-row > .mrl-arm { grid-column:1 / -1; margin-top:2px; }
.mrl-per { font-weight:600; font-variant-numeric:tabular-nums; display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
.mrl-amt { font-family:var(--font-mono); font-variant-numeric:tabular-nums; }
.mrl-rcv { color:var(--text-muted); line-height:1.4; min-width:0; overflow-wrap:anywhere; }
.mrl-rcv a { color:var(--accent); text-decoration:none; font-weight:600; }
.mrl-tags { display:flex; align-items:center; gap:6px; justify-content:flex-end; }
.mrl-tags .btn { min-height:30px; white-space:nowrap; }
.mrl-tags .kb-btn { color:var(--text-muted); }
.mrl-empty { padding:16px 12px; text-align:center; color:var(--text-muted); }
.mrl-form { display:grid; grid-template-columns:repeat(auto-fit, minmax(150px, 1fr)); gap:10px; padding:14px; border:1px solid var(--accent-border); border-radius:var(--radius); background:var(--card); box-shadow:var(--shadow-card); }
.mrl-form .mrl-wide { grid-column:1 / -1; }
.mrl-kinds { display:flex; flex-wrap:wrap; gap:6px; }
.mrl-check { display:flex; align-items:center; gap:8px; font-size:12.5px; color:var(--text); min-height:36px; cursor:pointer; }
.mrl-check input { width:18px; height:18px; accent-color:var(--accent); }
.mrl-form-foot { grid-column:1 / -1; display:flex; justify-content:flex-end; align-items:center; gap:8px; flex-wrap:wrap; }
.mrl-form-foot .mrl-hint { flex:1; min-width:180px; }
.mrl-form-foot .btn { min-height:36px; }
.mrl-err { padding:8px 10px; border-radius:var(--radius-sm); background:var(--danger-soft); color:var(--danger-text); line-height:1.45; font-size:12px; }
.mrl-hist summary { cursor:pointer; display:flex; align-items:center; gap:8px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.09em; color:var(--text-muted); min-height:30px; list-style:none; }
.mrl-hist summary::-webkit-details-marker { display:none; }
.mrl-hist summary::before { content:'▸'; font-size:11px; color:var(--text-faint); transition:transform .12s ease; }
.mrl-hist[open] summary::before { transform:rotate(90deg); }
.mrl-hist .mrl-cnt { display:inline-flex; align-items:center; justify-content:center; min-width:20px; height:20px; padding:0 6px; border-radius:var(--radius-pill); background:var(--bg-subtle); border:1px solid var(--border); color:var(--text); font-size:11px; letter-spacing:0; }
.mrl-hist ul { list-style:none; margin:8px 0 0; padding:0; display:flex; flex-direction:column; gap:7px; }
.mrl-hist li { display:flex; flex-direction:column; gap:1px; font-size:12px; line-height:1.4; overflow-wrap:anywhere; padding-bottom:7px; border-bottom:1px solid var(--border-soft); }
.mrl-hist li:last-child { border-bottom:none; }
.mrl-hist .meta { font-size:11px; color:var(--text-faint); }
.mrl-discard { display:flex; align-items:center; justify-content:flex-end; gap:8px 10px; flex-wrap:wrap; padding:10px 12px; border-radius:var(--radius-sm); background:var(--warning-soft); color:var(--warning-text); border:1px solid var(--warning-border); font-size:12.5px; margin-bottom:12px; }
.mrl-discard span { flex:1 1 auto; min-width:150px; font-weight:600; }
.mrl-discard .btn { min-height:34px; }
.mrl-bg { position:fixed; inset:0; background:rgba(15,23,42,.5); z-index:300; display:flex; align-items:center; justify-content:center; padding:12px; box-sizing:border-box; }
.mrl-modal { background:var(--bg); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-pop); width:min(720px, 100%); max-height:calc(100dvh - 24px); display:flex; flex-direction:column; overflow:hidden; }
.mrl-mhead { display:flex; align-items:center; gap:12px; padding:14px 16px; border-bottom:1px solid var(--border-soft); background:var(--card); }
.mrl-av { flex:none; width:40px; height:40px; border-radius:var(--radius-pill); background:var(--info-soft); color:var(--info-text); font-weight:700; font-size:14px; display:inline-flex; align-items:center; justify-content:center; }
.mrl-mtitle { flex:1; min-width:0; }
.mrl-mhead h3 { margin:0; font-size:16px; font-weight:700; color:var(--text); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.mrl-msub { font-size:12px; color:var(--text-muted); margin-top:1px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.mrl-x { flex:none; min-width:36px; height:36px; border:none; background:none; border-radius:var(--radius-pill); color:var(--text-muted); cursor:pointer; font-size:16px; }
.mrl-x:hover { background:var(--bg-subtle); color:var(--text); }
.mrl-mbody { padding:14px 16px 20px; overflow-y:auto; overscroll-behavior:contain; }
@media (max-width: 640px) {
  /* a row on a phone: month + amount · received / memo · the tags + actions on their own line */
  .mrl-list { display:block; }
  .mrl-list > .mrl-row, .mrl-row { grid-template-columns:minmax(0, 1fr) auto; }
  .mrl-amt { text-align:right; }
  .mrl-rcv { grid-column:1 / -1; }
  .mrl-tags { grid-column:1 / -1; justify-content:flex-start; flex-wrap:wrap; }
  .mrl-tags .kb-btn { margin-left:auto; }
  .mrl-in, .mrl-sel, .mrl-ta { font-size:16px; }
  .mrl-facts { gap:8px; }
  .mrl-dv { font-size:12.5px; }
  .mrl-form-foot .mrl-hint { flex:1 1 100%; }
  .mrl-fu { grid-template-columns:1fr 1fr; }
  .mrl-tog, .mrl-note-f .btn, .mrl-tags .btn, .mrl-arm .btn, .mrl-row-acts .btn { min-height:36px; }
  .mrl-note-h .kb-btn, .mrl-tags .kb-btn { min-width:36px; height:36px; }
  .mrl-bg { align-items:flex-end; padding:0; }
  .mrl-modal { width:100%; max-height:94dvh; border-radius:var(--radius) var(--radius) 0 0; }
  .mrl-mbody { padding:12px 12px 20px; }
  .mrl-form-foot .btn { flex:1; justify-content:center; }
}
`;
function ensureCss(): void {
  if (document.getElementById(CSS_ID)) return;
  const st = document.createElement('style');
  st.id = CSS_ID; st.textContent = CSS;
  document.head.appendChild(st);
}

const pill = (tone: string, text: string, title = '', extra = '') => `<span class="mrl-pill ${esc(tone)}${extra ? ' ' + extra : ''}"${title ? ` title="${esc(title)}"` : ''}>${esc(text)}</span>`;
const dueDateOf = (period: string, dueDay: unknown) => {
  const [y, m] = period.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const d = Math.max(1, Math.min(Number(dueDay) || 1, last));
  return `${period}-${String(d).padStart(2, '0')}`;
};
const kindLabel = (k: unknown) => (PAY_KINDS.find(x => x.key === k) || PAY_KINDS[0]).label;
const kindOf = (p: any) => (p && ['deposit', 'fee', 'other'].includes(p.kind) ? p.kind : 'rent');
const isClearing = (p: any) => !!p && p.status === 'clearing';

interface Draft { text: string; fu: boolean; from: string; to: string; period: string }
interface EditDraft extends Draft { id: string; orig: string }
interface PayDraft { period: string; kind: string; amount: string; received_at: string; clearing: boolean; expected_clear: string; note: string }

/** Renders the tenant panel into `host` (which the caller owns) and binds it. */
export function mountRentLedger(host: HTMLElement, opts: RentLedgerOpts): { redraw: () => void; isDirty: () => boolean } {
  ensureCss();
  const who = (by: unknown) => (by ? (opts.whoName ? opts.whoName(String(by)) : whoFrom(null, by)) : '');
  let busy = '';
  let noteErr = '', payErr = '', rowErr = '';
  const compose: Draft = { text: '', fu: false, from: '', to: '', period: '' };
  let edit: EditDraft | null = null;
  let noteArm: string | null = null;
  let rowArm: string | null = null;
  let payOpen = opts.focus === 'pay';
  let pay: PayDraft | null = null;
  let payTouched = false;
  let histOpen = false;
  let lastDirty = false;
  let focusNext: string | null = opts.focus === 'note' ? 'compose' : opts.focus === 'pay' ? 'pay-amount' : null;

  const isDirty = () => !!(compose.text.trim() || (edit && (edit.text.trim() !== edit.orig.trim())) || (payOpen && payTouched));
  const syncDirty = () => { const d = isDirty(); if (d !== lastDirty) { lastDirty = d; opts.onDirty?.(d); } };

  const freshPay = (t: any, period?: string): PayDraft => {
    const ps = t.payment_status || {};
    const per = period || ((ps.owed && ps.owed.length) ? ps.owed[0] : localToday().slice(0, 7));
    return { period: per, kind: 'rent', amount: t.monthly_amount != null ? String(t.monthly_amount) : '', received_at: localToday(), clearing: false, expected_clear: addDays(localToday(), 7), note: '' };
  };
  const monthsFor = (t: any): string[] => {
    // the months a note can be filed under: the lease (≤ 18), else the last 6 + the next 2
    const now = localToday().slice(0, 7);
    const start = t.lease_start ? String(t.lease_start).slice(0, 7) : addMonths(now, -6);
    const endRaw = t.lease_end ? String(t.lease_end).slice(0, 7) : addMonths(now, 2);
    const out: string[] = [];
    let p = endRaw < start ? start : endRaw;
    while (p >= start && out.length < 18) { out.push(p); p = addMonths(p, -1); }
    return out;
  };

  // ── render ──────────────────────────────────────────────────────────────
  const noteHtml = (n: any, t: any): string => {
    const anon = !n.by;
    const editing = !!(edit && edit.id === n.id);
    const f = n.follow || null;
    const edited = n.edited_at ? ` · edited${n.edited_by && String(n.edited_by).toLowerCase() !== String(n.by || '').toLowerCase() ? ` by ${esc(who(n.edited_by))}` : ''}` : '';
    const items: KebabItem[] = [];
    if (n.can_edit && !editing) items.push({ icon: '✎', label: 'Edit note', attrs: { 'data-mrl-note-edit': n.id } });
    if (n.follow_up && n.done) items.push({ icon: '↺', label: 'Reopen the follow-up', attrs: { 'data-mrl-note-reopen': n.id } });
    if (n.can_edit && !editing) items.push({ icon: '🗑', label: 'Delete note', danger: true, attrs: { 'data-mrl-note-del': n.id } });
    const menu = items.length ? kebabHtml(items, { tip: 'Note actions' }) : '';
    const tag = n.period ? pill('info', periodLabel(n.period), 'Filed under this rent month') : '';
    let body: string;
    if (editing && edit) {
      body = `<textarea class="mrl-ta" data-mrl-edit="text" data-mrl-fk="edit" maxlength="4000" aria-label="Edit note">${esc(edit.text)}</textarea>
        ${draftOpts(edit, t, 'edit')}
        <div class="mrl-row-acts"><span class="mrl-hint">⌘/Ctrl + Enter saves · Esc cancels</span>
          <button type="button" class="btn btn-ghost btn-sm" data-mrl-act="edit-cancel">Cancel</button>
          <button type="button" class="btn btn-secondary btn-sm mrl-add" data-mrl-act="edit-save"${busy ? ' disabled' : ''}>${busy === 'edit' ? 'Saving…' : 'Save'}</button></div>`;
    } else {
      body = `${n.context ? `<div class="mrl-note-ctx">↳ with: ${esc(n.context)}</div>` : ''}<div class="mrl-note-t">${esc(n.text)}</div>`;
    }
    let foot = '';
    if (!editing && n.follow_up) {
      if (n.done) {
        foot = `<div class="mrl-note-f">${pill('ok', `✓ checked${n.done_by ? ` · ${who(n.done_by)}` : ''}${n.done_at ? ` · ${md(String(n.done_at).slice(0, 10))}` : ''}`, `follow-up ${windowLabel(n.follow_up_from, n.follow_up)} — checked`)}</div>`;
      } else {
        const label = f ? followText(f) : `⏰ check ${windowLabel(n.follow_up_from, n.follow_up)}`;
        foot = `<div class="mrl-note-f">${pill(followTone(f && f.tone), label, 'follow-up — anyone on the office team can check it off')}
          <button type="button" class="btn btn-secondary btn-sm" data-mrl-tick="${esc(n.id)}"${busy ? ' disabled' : ''}>${busy === 'tick:' + n.id ? 'Saving…' : '✓ Mark checked'}</button>
          ${n.period_paid ? `<span class="mrl-nudge">${esc(periodLabel(n.period))} is now logged — mark this checked?</span>` : ''}</div>`;
      }
    }
    const arm = noteArm === n.id ? `<div class="mrl-arm" role="alert"><span>Delete this note? It can’t be undone — the history keeps only “note deleted”.</span><button type="button" class="btn btn-ghost btn-sm" data-mrl-act="note-keep">Keep</button><button type="button" class="btn btn-danger btn-sm" data-mrl-note-delyes="${esc(n.id)}"${busy ? ' disabled' : ''}>Delete</button></div>` : '';
    const fuCls = !editing && f && !n.done ? ` fu-${f.tone}` : '';
    return `<div class="mrl-note${editing ? ' editing' : ''}${fuCls}" data-mrl-note="${esc(n.id)}">
      <div class="mrl-note-h"><span class="who${anon ? ' anon' : ''}">${esc(anon ? 'author not recorded' : who(n.by))}</span><span title="${esc(fmtWhen(n.at))}">· ${esc(ago(n.at))}${edited}</span>${tag}${menu}</div>
      ${body}${foot}${arm}
    </div>`;
  };
  const draftOpts = (d: Draft, t: any, key: 'compose' | 'edit'): string => {
    const months = monthsFor(t);
    if (d.period && !months.includes(d.period)) months.unshift(d.period);
    return `<div class="mrl-opts">
        <button type="button" class="mrl-tog${d.fu ? ' on' : ''}" data-mrl-fu-tog="${key}" aria-pressed="${d.fu ? 'true' : 'false'}" title="A date to check back — shows on the card and the Calendar until someone marks it checked">⏰ Follow-up date</button>
        <label class="mrl-tog${d.period ? ' on' : ''}" style="padding-right:4px;">📅 <select class="mrl-sel" data-mrl-${key}="period" aria-label="File the note under a rent month (optional)" style="border:none; background:transparent; min-height:28px; padding:0 4px; width:auto; color:inherit; font-weight:600;">
          <option value="">No month</option>${months.map(p => `<option value="${esc(p)}"${p === d.period ? ' selected' : ''}>About ${esc(periodLabel(p))}</option>`).join('')}</select></label>
      </div>
      ${d.fu ? `<div class="mrl-fu">
        <label class="mrl-lab">From (optional)<input class="mrl-in" type="date" data-mrl-${key}="from" data-mrl-fk="${key}-from" value="${esc(d.from)}"></label>
        <label class="mrl-lab">Check by<input class="mrl-in" type="date" data-mrl-${key}="to" data-mrl-fk="${key}-to" value="${esc(d.to)}"></label>
      </div>` : ''}`;
  };

  const rowsHtml = (t: any): { html: string; count: number } => {
    const ps = t.payment_status || {};
    const pays: any[] = (Array.isArray(t.payments) ? t.payments : []).filter((p: any) => p && p.period);
    const notesBy = new Map<string, number>();
    for (const n of (Array.isArray(t.notes) ? t.notes : [])) if (n && n.period) notesBy.set(n.period, (notesBy.get(n.period) || 0) + 1);
    type R = { per: string; order: number; html: string };
    const rows: R[] = [];
    const noteChip = (per: string) => (notesBy.get(per) ? `<span class="mrl-pill muted" title="notes filed under this month">💬 ${notesBy.get(per)}</span>` : '');
    for (const per of (Array.isArray(ps.owed) ? ps.owed : [])) {
      rows.push({ per, order: 0, html: `<div class="mrl-row owed" data-mrl-owed="${esc(per)}">
        <span class="mrl-per">${esc(periodLabel(per))} ${noteChip(per)}</span>
        <span class="mrl-amt">${money(t.monthly_amount)}</span>
        <span class="mrl-rcv">not logged yet · due ${esc(md(dueDateOf(per, t.due_day)))}</span>
        <span class="mrl-tags">${pill('bad', 'owed')}<button type="button" class="btn btn-secondary btn-sm" data-mrl-open-pay="${esc(per)}"${busy ? ' disabled' : ''}>Log</button></span>
      </div>` });
    }
    for (const p of pays) {
      const k = kindOf(p);
      const clearing = isClearing(p);
      const proof = safeUrl(p.proof_url);
      const tag = clearing
        ? pill(p.expected_clear && p.expected_clear < localToday() ? 'bad' : 'warn', `clearing${p.expected_clear ? ` · by ${md(p.expected_clear)}` : ''}`, 'received, not cleared yet — counts as not paid until Cleared')
        : k !== 'rent' ? '' : p.on_time === true ? pill('ok', 'on time') : p.on_time === false ? pill('bad', 'late') : '';
      const items: KebabItem[] = [];
      if (clearing) items.push({ icon: '✓', label: 'Cleared', attrs: { 'data-mrl-clear': p.id } });
      else items.push({ icon: '⏳', label: 'Mark as still clearing', attrs: { 'data-mrl-unclear': p.id } });
      items.push({ divider: true });
      for (const kd of PAY_KINDS) if (kd.key !== k) items.push({ icon: '↪', label: `File as ${kd.label.toLowerCase()}`, attrs: { 'data-mrl-refile': `${p.id}|${kd.key}` } });
      items.push({ divider: true }, { icon: '🗑', label: 'Delete record…', danger: true, attrs: { 'data-mrl-del': p.id } });
      const arm = rowArm === p.id ? `<div class="mrl-arm" role="alert"><span>Delete the ${esc(periodLabel(p.period))} ${esc(kindLabel(k).toLowerCase())} record?</span><button type="button" class="btn btn-ghost btn-sm" data-mrl-act="row-keep">Keep</button><button type="button" class="btn btn-danger btn-sm" data-mrl-delyes="${esc(p.id)}"${busy ? ' disabled' : ''}>Delete</button></div>` : '';
      rows.push({ per: p.period, order: k === 'rent' ? 1 : 2, html: `<div class="mrl-row${clearing ? ' clearing' : ''}" data-mrl-row="${esc(p.id)}">
        <span class="mrl-per">${esc(periodLabel(p.period))}${k !== 'rent' ? ` ${pill('muted', kindLabel(k), 'not rent — never pays a month', 'kind')}` : ''} ${noteChip(p.period)}</span>
        <span class="mrl-amt">${money(p.amount)}</span>
        <span class="mrl-rcv">received ${esc(fmtFull(p.received_at))}${k === 'rent' && p.due_date ? ` · due ${esc(md(p.due_date))}` : ''}${proof ? ` · <a href="${esc(proof)}" target="_blank" rel="noopener">🧾 proof</a>` : ''}${p.note ? ` · ${esc(String(p.note).slice(0, 80))}` : ''}${p.cleared_at ? ` · cleared ${esc(md(String(p.cleared_at).slice(0, 10)))}` : ''}</span>
        <span class="mrl-tags">${tag}${clearing ? `<button type="button" class="btn btn-secondary btn-sm" data-mrl-clear="${esc(p.id)}"${busy ? ' disabled' : ''}>${busy === 'clear:' + p.id ? 'Saving…' : '✓ Cleared'}</button>` : ''}${kebabHtml(items, { tip: 'Payment actions' })}</span>
        ${arm}
      </div>` });
    }
    rows.sort((a, b) => (a.per < b.per ? 1 : a.per > b.per ? -1 : a.order - b.order));
    return { html: rows.map(r => r.html).join(''), count: pays.length };
  };

  const payFormHtml = (t: any): string => {
    if (!pay) pay = freshPay(t);
    const p = pay;
    return `<div class="mrl-form" data-mrl-payform>
      <label class="mrl-lab">Month<input class="mrl-in" type="month" data-mrl-f="period" value="${esc(p.period)}"></label>
      <label class="mrl-lab">Amount $<input class="mrl-in" type="number" inputmode="decimal" min="0" step="0.01" data-mrl-f="amount" data-mrl-fk="pay-amount" value="${esc(p.amount)}" placeholder="${esc(t.monthly_amount ?? 'amount')}"></label>
      <label class="mrl-lab">Received<input class="mrl-in" type="date" data-mrl-f="received_at" value="${esc(p.received_at)}" max="${esc(addDays(localToday(), 1))}"></label>
      <div class="mrl-lab mrl-wide">What is it?<div class="mrl-kinds" role="radiogroup" aria-label="Payment kind">${PAY_KINDS.map(k => `<button type="button" class="chip${p.kind === k.key ? ' active' : ''}" role="radio" aria-checked="${p.kind === k.key ? 'true' : 'false'}" data-mrl-kind="${k.key}">${esc(k.label)}</button>`).join('')}</div></div>
      <label class="mrl-check mrl-wide"><input type="checkbox" data-mrl-f="clearing"${p.clearing ? ' checked' : ''}> A check that hasn’t cleared yet</label>
      ${p.clearing ? `<label class="mrl-lab">Expected to clear<input class="mrl-in" type="date" data-mrl-f="expected_clear" value="${esc(p.expected_clear)}"></label>` : ''}
      <label class="mrl-lab">Proof screenshot<input class="mrl-in" type="file" accept="image/*" data-mrl-file></label>
      <label class="mrl-lab mrl-wide">Memo<input class="mrl-in" type="text" data-mrl-f="note" value="${esc(p.note)}" placeholder="Oct rent, SD, CF… (optional)"></label>
      ${payErr ? `<div class="mrl-err mrl-wide" role="alert">${esc(payErr)}</div>` : ''}
      <div class="mrl-form-foot"><span class="mrl-hint">${p.kind === 'rent' ? (p.clearing ? 'Shows as “clearing” — not paid — until someone taps Cleared.' : 'On-time = received on or before that month’s due date.') : 'A deposit or fee never counts as that month’s rent.'}</span>
        <button type="button" class="btn btn-ghost" data-mrl-act="pay-cancel">Cancel</button>
        <button type="button" class="btn btn-primary" data-mrl-log${busy ? ' disabled' : ''}>${busy === 'pay' ? 'Saving…' : 'Save payment'}</button></div>
    </div>`;
  };

  const draw = (): void => {
    closeKebab();
    const t = opts.getTenant();
    if (!t) { host.innerHTML = '<div class="mrl"><div class="mrl-empty">This tenant is no longer on the roster.</div></div>'; return; }
    // keep focus + caret across the repaint
    const ae = document.activeElement as HTMLInputElement | null;
    const fk = ae && host.contains(ae) ? (ae.getAttribute('data-mrl-fk') || '') : '';
    const selA = fk && typeof ae!.selectionStart === 'number' ? ae!.selectionStart : null;
    const selB = fk && typeof ae!.selectionEnd === 'number' ? ae!.selectionEnd : null;
    const ps = t.payment_status || {};
    const chip = chipOf(ps, !!t.unsaved);
    const fu = t.follow_up || null;
    const notes: any[] = (Array.isArray(t.notes) ? t.notes : []).filter((n: any) => n && n.id).slice().reverse();
    const fuCnt = t.follow_ups || { open: 0, due: 0, overdue: 0 };
    const clearingRow = (Array.isArray(t.payments) ? t.payments : []).filter(isClearing).sort((a: any, b: any) => String(a.expected_clear || '9').localeCompare(String(b.expected_clear || '9')))[0] || null;
    const owedFirst = Array.isArray(ps.owed) && ps.owed.length ? ps.owed[0] : null;
    const primary = payOpen ? '' : clearingRow
      ? `<button type="button" class="btn btn-primary mrl-primary" data-mrl-clear="${esc(clearingRow.id)}"${busy ? ' disabled' : ''}>${busy === 'clear:' + clearingRow.id ? 'Saving…' : `✓ ${esc(periodLabel(clearingRow.period))} ${kindOf(clearingRow) === 'rent' ? 'check' : esc(kindLabel(kindOf(clearingRow)).toLowerCase())} cleared`}</button>`
      : `<button type="button" class="btn btn-primary mrl-primary" data-mrl-open-pay="${esc(owedFirst || '')}"${busy ? ' disabled' : ''}>${owedFirst ? `Log the ${esc(periodLabel(owedFirst))} payment` : 'Log a payment'}</button>`;
    const auto = t.source === 'auto';
    const rows = rowsHtml(t);
    const hist: any[] = (Array.isArray(t.history) ? t.history : []).slice().reverse();
    const canAdd = !!compose.text.trim() && !busy;
    const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : '');
    host.innerHTML = `<div class="mrl">
      ${primary}
      <div class="mrl-card mrl-sum">
        <div class="mrl-sum-top"><span class="mrl-hav" aria-hidden="true">🏠</span>
          <div class="mrl-sum-txt"><div class="mrl-sum-home">${esc(t.listing_name || 'Home')}</div><div class="mrl-sum-sub">${auto ? 'from the Hostaway booking' : 'direct lease'}${t.status === 'past' ? ' · past tenancy' : ''}</div></div>
        </div>
        <div class="mrl-pills">${pill(chip.tone, chip.label, chip.title)}${fu ? pill(followTone(fu.tone), followText(fu), 'the earliest open follow-up') : ''}${ps.next_due ? pill('muted', `next due ${md(ps.next_due)}`) : ''}</div>
        <div class="mrl-facts">
          <div><div class="mrl-dl">Lease</div><div class="mrl-dv">${esc(md(t.lease_start))} → ${t.lease_end ? esc(md(t.lease_end)) : 'open'}</div></div>
          <div><div class="mrl-dl">Rent</div><div class="mrl-dv">${t.monthly_amount != null ? `${money(t.monthly_amount)} <small>· day ${esc(t.due_day || 1)}</small>` : '<small>not set</small>'}</div></div>
          <div><div class="mrl-dl">On time</div><div class="mrl-dv">${ps.on_time || ps.late ? `${esc(ps.on_time || 0)} <small>of ${esc((ps.on_time || 0) + (ps.late || 0))}</small>` : '<small>—</small>'}</div></div>
        </div>
        ${auto ? '<div class="mrl-fine">The lease follows the Hostaway booking — <b>extend it in Hostaway</b>; a note here can say why it isn’t extended yet.</div>' : ''}
      </div>

      <div class="mrl-sec">Notes <span class="mrl-cnt">${notes.length}</span>${fuCnt.open ? `<span class="mrl-cnt ${fuCnt.overdue ? 'bad' : fuCnt.due ? 'warn' : ''}" title="open follow-ups">⏰ ${esc(fuCnt.open)}</span>` : ''}</div>
      <div class="mrl-card mrl-compose">
        <textarea class="mrl-ta" data-mrl-compose="text" data-mrl-fk="compose" maxlength="4000" placeholder="Why a payment isn’t logged yet, why the lease isn’t extended, what happens next…" aria-label="New note">${esc(compose.text)}</textarea>
        ${draftOpts(compose, t, 'compose')}
        ${noteErr ? `<div class="mrl-err" role="alert">${esc(noteErr)}</div>` : ''}
        <div class="mrl-row-acts"><span class="mrl-hint">Office team only · don’t paste card or bank numbers · ⌘/Ctrl + Enter adds</span>
          <button type="button" class="btn btn-secondary btn-sm mrl-add" data-mrl-act="add-note"${canAdd ? '' : ' disabled'}>${busy === 'note' ? 'Saving…' : 'Add note'}</button></div>
      </div>
      ${notes.length ? `<div class="mrl-notes">${notes.map(n => noteHtml(n, t)).join('')}</div>` : ''}

      <div class="mrl-sec">Rent <span class="mrl-cnt" title="payment records">${rows.count}</span>${ps.clearing_count ? `<span class="mrl-cnt warn" title="months with a check clearing">⏳ ${esc(ps.clearing_count)}</span>` : ''}${payOpen ? '' : `<span class="mrl-sec-r"><button type="button" class="btn btn-ghost btn-sm" data-mrl-open-pay=""${busy ? ' disabled' : ''}>＋ Log payment</button></span>`}</div>
      ${payOpen ? payFormHtml(t) : ''}
      ${rowErr ? `<div class="mrl-err" role="alert">${esc(rowErr)}</div>` : ''}
      <div class="mrl-list">${rows.html || '<div class="mrl-empty">No payments logged yet.</div>'}</div>

      <details class="mrl-hist" data-mrl-hist${histOpen ? ' open' : ''}>
        <summary>History <span class="mrl-cnt">${hist.length}</span></summary>
        ${hist.length ? `<ul>${hist.map(h => `<li><span>${esc(cap(String(h.note || h.kind || '')))}</span><span class="meta">${esc(h.by ? who(h.by) : '—')}${h.at ? ` · ${esc(fmtWhen(h.at))}` : ''}</span></li>`).join('')}</ul>` : `<div class="mrl-hint" style="padding:6px 0;">${Array.isArray(t.history) ? 'Nothing recorded yet.' : 'Loading…'}</div>`}
      </details>
    </div>`;
    // per-element binds for ⋯ items (an open menu is MOVED to <body>, out of the host's delegated listener)
    bindKebabs(host);
    const on = (sel: string, fn: (el: HTMLElement) => void) => host.querySelectorAll<HTMLElement>(sel).forEach(el => el.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); closeKebab(); fn(el); }));
    on('[data-mrl-note-edit]', (el) => startEdit(el.dataset.mrlNoteEdit!));
    on('[data-mrl-note-del]', (el) => { noteArm = el.dataset.mrlNoteDel!; rowArm = null; setTimeout(draw, 0); });
    on('[data-mrl-note-reopen]', (el) => { void tick(el.dataset.mrlNoteReopen!, false); });
    on('.kb-menu [data-mrl-clear]', (el) => { void clear(el.dataset.mrlClear!); });
    on('[data-mrl-unclear]', (el) => { void putPay(el.dataset.mrlUnclear!, { cleared: false }, 'unclear'); });
    on('[data-mrl-refile]', (el) => { const [pid, k] = el.dataset.mrlRefile!.split('|'); void putPay(pid, { kind: k }, 'refile'); });
    on('[data-mrl-del]', (el) => { rowArm = el.dataset.mrlDel!; noteArm = null; setTimeout(draw, 0); });
    // focus
    const want = focusNext || fk;
    focusNext = null;
    if (want) {
      const el = host.querySelector<HTMLInputElement>(`[data-mrl-fk="${want}"]`);
      if (el) {
        try { el.focus({ preventScroll: want === fk }); } catch { /* old engine */ }
        if (want === fk && selA != null && selB != null) { try { el.setSelectionRange(selA, selB); } catch { /* not a text field */ } }
        if (want !== fk) el.scrollIntoView({ block: 'nearest' });
      }
    }
    syncDirty();
  };

  // ── writes ──────────────────────────────────────────────────────────────
  const apply = async (j: any): Promise<void> => {
    if (j && j.tenant && j.tenant.id && opts.applyTenant) opts.applyTenant(j.tenant);
    else { try { await opts.refresh(); } catch { /* the write landed; a failed refresh only means a stale view */ } }
    opts.onChanged?.();
  };
  const send = async (method: 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<{ ok: boolean; status: number; j: any }> => {
    try {
      const r = await fetch(`/api/mtr/tenants/${encodeURIComponent(opts.tenantId)}${path}`, {
        method,
        headers: body === undefined || body instanceof FormData ? undefined : { 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      return { ok: r.ok, status: r.status, j: j || {} };
    } catch { return { ok: false, status: 0, j: { error: 'Network error — nothing was saved.' } }; }
  };
  const errOf = (r: { status: number; j: any }, what: string) => (r.j && r.j.error ? String(r.j.error) : r.status ? `${what} didn’t save (${r.status}).` : 'Network error — nothing was saved.');

  const draftBody = (d: Draft) => ({ follow_up: d.fu && d.to ? d.to : '', follow_up_from: d.fu && d.to && d.from ? d.from : '', period: d.period || '' });
  const checkDraft = (d: Draft): string => {
    if (d.fu && !d.to) return 'Pick the “check by” date (or turn the follow-up off).';
    if (d.fu && d.from && d.to && d.from > d.to) return 'The window starts after it ends.';
    return '';
  };
  async function addNote(): Promise<void> {
    if (busy || !compose.text.trim()) return;
    const bad = checkDraft(compose);
    if (bad) { noteErr = bad; draw(); return; }
    const b = draftBody(compose);
    busy = 'note'; noteErr = ''; draw();
    const r = await send('POST', '/notes', { text: compose.text.trim(), ...(b.follow_up ? { follow_up: b.follow_up } : {}), ...(b.follow_up_from ? { follow_up_from: b.follow_up_from } : {}), ...(b.period ? { period: b.period } : {}) });
    busy = '';
    if (r.ok) { compose.text = ''; compose.fu = false; compose.from = ''; compose.to = ''; compose.period = ''; await apply(r.j); }
    else noteErr = errOf(r, 'The note') + ' It is still in the box.';
    draw();
  }
  function startEdit(id: string): void {
    const t = opts.getTenant();
    const n = (t && Array.isArray(t.notes) ? t.notes : []).find((x: any) => x && x.id === id);
    if (!n) return;
    edit = { id, text: String(n.text || ''), orig: String(n.text || ''), fu: !!n.follow_up, from: n.follow_up_from || '', to: n.follow_up || '', period: n.period || '' };
    focusNext = 'edit';
    setTimeout(draw, 0);
  }
  async function saveEdit(): Promise<void> {
    if (busy || !edit) return;
    if (!edit.text.trim()) { noteErr = 'A note can’t be empty — use ⋯ → Delete note to remove it.'; draw(); return; }
    const bad = checkDraft(edit);
    if (bad) { noteErr = bad; draw(); return; }
    const ed = edit;
    busy = 'edit'; noteErr = ''; draw();
    const r = await send('PUT', `/notes/${encodeURIComponent(ed.id)}`, { text: ed.text.trim(), ...draftBody(ed) });
    busy = '';
    if (r.ok) { if (edit === ed) edit = null; await apply(r.j); }
    else noteErr = errOf(r, 'The edit');
    draw();
  }
  async function tick(id: string, done: boolean): Promise<void> {
    if (busy) return;
    busy = 'tick:' + id; noteErr = ''; draw();
    const r = await send('PUT', `/notes/${encodeURIComponent(id)}`, { done });
    busy = '';
    if (r.ok) await apply(r.j); else noteErr = errOf(r, 'The follow-up');
    draw();
  }
  async function delNote(id: string): Promise<void> {
    if (busy) return;
    busy = 'delnote'; noteErr = ''; draw();
    const r = await send('DELETE', `/notes/${encodeURIComponent(id)}`);
    busy = '';
    if (r.ok) { noteArm = null; if (edit && edit.id === id) edit = null; await apply(r.j); }
    else noteErr = errOf(r, 'Deleting the note');
    draw();
  }
  async function clear(pid: string): Promise<void> { await putPay(pid, { cleared: true }, 'clear:' + pid); }
  async function putPay(pid: string, body: Record<string, unknown>, tag: string): Promise<void> {
    if (busy) return;
    busy = tag; rowErr = ''; draw();
    const r = await send('PUT', `/payment/${encodeURIComponent(pid)}`, body);
    busy = '';
    if (r.ok) await apply(r.j); else rowErr = errOf(r, 'The payment');
    draw();
  }
  async function delPay(pid: string): Promise<void> {
    if (busy) return;
    busy = 'delpay'; rowErr = ''; draw();
    const r = await send('DELETE', `/payment/${encodeURIComponent(pid)}`);
    busy = '';
    if (r.ok) { rowArm = null; await apply(r.j); } else rowErr = errOf(r, 'Deleting the record') + ' Nothing was removed.';
    draw();
  }
  async function logPay(): Promise<void> {
    if (busy || !pay) return;
    const p = pay;
    if (!/^\d{4}-\d{2}$/.test(p.period)) { payErr = 'Pick the month this payment covers.'; draw(); return; }
    if (p.amount !== '' && !(Number(p.amount) >= 0)) { payErr = 'The amount must be a number.'; draw(); return; }
    if (p.received_at && p.received_at > addDays(localToday(), 1)) { payErr = 'The received date is in the future — if the check is on its way, tick “hasn’t cleared yet” and set when it should clear.'; draw(); return; }
    const fd = new FormData();
    fd.append('period', p.period);
    if (p.amount !== '') fd.append('amount', p.amount);
    if (p.received_at) fd.append('received_at', p.received_at);
    if (p.kind && p.kind !== 'rent') fd.append('kind', p.kind);
    if (p.clearing) { fd.append('status', 'clearing'); if (p.expected_clear) fd.append('expected_clear', p.expected_clear); }
    if (p.note) fd.append('note', p.note);
    const file = host.querySelector<HTMLInputElement>('[data-mrl-file]')?.files?.[0];
    if (file) fd.append('file', file);
    busy = 'pay'; payErr = ''; rowArm = null; draw();
    const r = await send('POST', '/payment', fd);
    busy = '';
    if (r.ok) { payOpen = false; pay = null; payTouched = false; await apply(r.j); }
    else payErr = errOf(r, 'The payment');
    draw();
  }

  // ── events (ONE delegated set on the host) ─────────────────────────────
  host.addEventListener('click', (e) => {
    const el = e.target as HTMLElement;
    if (el.closest('.kb-menu')) return;   // ⋯ items are bound per element in draw()
    const act = el.closest<HTMLElement>('[data-mrl-act]');
    const t = opts.getTenant();
    if (act && !(act as HTMLButtonElement).disabled) {
      switch (act.dataset.mrlAct) {
        case 'add-note': void addNote(); return;
        case 'edit-save': void saveEdit(); return;
        case 'edit-cancel': edit = null; noteErr = ''; draw(); return;
        case 'note-keep': noteArm = null; draw(); return;
        case 'row-keep': rowArm = null; draw(); return;
        case 'pay-cancel': payOpen = false; pay = null; payTouched = false; payErr = ''; draw(); return;
      }
    }
    const fuTog = el.closest<HTMLElement>('[data-mrl-fu-tog]');
    if (fuTog) {
      const d = fuTog.dataset.mrlFuTog === 'edit' ? edit : compose;
      if (!d) return;
      d.fu = !d.fu;
      if (d.fu && !d.to) d.to = addDays(localToday(), 3);
      draw();
      return;
    }
    const logBtn = el.closest<HTMLElement>('[data-mrl-log]');
    if (logBtn && !(logBtn as HTMLButtonElement).disabled) { void logPay(); return; }
    const tickBtn = el.closest<HTMLElement>('[data-mrl-tick]');
    if (tickBtn && !(tickBtn as HTMLButtonElement).disabled) { void tick(tickBtn.dataset.mrlTick!, true); return; }
    const yes = el.closest<HTMLElement>('[data-mrl-note-delyes]');
    if (yes && !(yes as HTMLButtonElement).disabled) { void delNote(yes.dataset.mrlNoteDelyes!); return; }
    const pyes = el.closest<HTMLElement>('[data-mrl-delyes]');
    if (pyes && !(pyes as HTMLButtonElement).disabled) { void delPay(pyes.dataset.mrlDelyes!); return; }
    const clr = el.closest<HTMLElement>('[data-mrl-clear]');
    if (clr && !(clr as HTMLButtonElement).disabled) { void clear(clr.dataset.mrlClear!); return; }
    const openPay = el.closest<HTMLElement>('[data-mrl-open-pay]');
    if (openPay && !(openPay as HTMLButtonElement).disabled && t) {
      payOpen = true; payTouched = false; payErr = '';
      pay = freshPay(t, openPay.dataset.mrlOpenPay || undefined);
      focusNext = 'pay-amount';
      draw();
      return;
    }
    const kindBtn = el.closest<HTMLElement>('[data-mrl-kind]');
    if (kindBtn && pay) {
      const k = kindBtn.dataset.mrlKind!;
      if (k !== pay.kind) {
        // the amount follows the kind only while it is still the untouched rent default
        if (t && pay.amount === String(t.monthly_amount ?? '') && k !== 'rent') pay.amount = '';
        else if (t && pay.amount === '' && k === 'rent' && t.monthly_amount != null) pay.amount = String(t.monthly_amount);
        pay.kind = k; payTouched = true; draw();
      }
      return;
    }
    if ((noteArm || rowArm) && !el.closest('button, a, input, select, textarea, label')) { noteArm = null; rowArm = null; draw(); }
  });
  host.addEventListener('input', (e) => {
    const el = e.target as HTMLInputElement;
    if (el.matches('[data-mrl-compose]')) {
      const k = el.dataset.mrlCompose as keyof Draft;
      (compose as any)[k] = el.value;
      if (k === 'text') { const b = host.querySelector<HTMLButtonElement>('[data-mrl-act="add-note"]'); if (b) b.disabled = !el.value.trim() || !!busy; }
    } else if (el.matches('[data-mrl-edit]') && edit) {
      (edit as any)[el.dataset.mrlEdit as string] = el.value;
    } else if (el.matches('[data-mrl-f]') && pay) {
      const k = el.dataset.mrlF as keyof PayDraft;
      if (k === 'clearing') return;   // a change event (redraws)
      (pay as any)[k] = el.value;
      payTouched = true;
    }
    syncDirty();
  });
  host.addEventListener('change', (e) => {
    const el = e.target as HTMLInputElement;
    if (el.matches('[data-mrl-f="clearing"]') && pay) { pay.clearing = el.checked; payTouched = true; if (pay.clearing && !pay.expected_clear) pay.expected_clear = addDays(pay.received_at || localToday(), 7); draw(); return; }
    if (el.matches('select[data-mrl-compose], select[data-mrl-edit]')) { draw(); return; }
    if (el.matches('[data-mrl-file]')) { payTouched = true; syncDirty(); }
  });
  host.addEventListener('keydown', (e) => {
    const el = e.target as HTMLElement;
    const submit = e.key === 'Enter' && (e.metaKey || e.ctrlKey);
    if (el.matches('[data-mrl-compose="text"]') && submit) { e.preventDefault(); void addNote(); }
    else if (el.matches('[data-mrl-edit="text"]')) {
      if (submit) { e.preventDefault(); void saveEdit(); }
      else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); edit = null; draw(); }
    }
  });
  host.addEventListener('toggle', (e) => { const d = e.target as HTMLDetailsElement; if (d && d.matches && d.matches('[data-mrl-hist]')) histOpen = d.open; }, true);
  draw();
  return { redraw: () => draw(), isDirty };
}

/** The modal shell (the panel + the tenant Edit / Add sheets): avatar head, ✕, a discard guard when something is typed. */
export function openMtrSheet(o: { title: string; sub?: string; avatar?: string; label?: string; isDirty?: () => boolean; onClose?: () => void }): { body: HTMLElement; close: (force?: boolean) => void; root: HTMLElement } {
  ensureCss();
  const bg = document.createElement('div');
  bg.className = 'mrl-bg';
  bg.innerHTML = `<div class="mrl-modal" role="dialog" aria-modal="true" aria-label="${esc(o.label || o.title)}">
    <div class="mrl-mhead">${o.avatar ? `<span class="mrl-av" aria-hidden="true">${esc(o.avatar)}</span>` : ''}<div class="mrl-mtitle"><h3>${esc(o.title)}</h3>${o.sub ? `<div class="mrl-msub">${esc(o.sub)}</div>` : ''}</div><button type="button" class="mrl-x" data-mrl-close aria-label="Close" title="Close (Esc)">✕</button></div>
    <div class="mrl-mbody"><div data-mrl-guard></div><div data-mrl-sheet-body></div></div>
  </div>`;
  document.body.appendChild(bg);
  const guard = bg.querySelector<HTMLElement>('[data-mrl-guard]')!;
  let closed = false;
  const close = (force = false) => {
    if (closed) return;
    if (!force && o.isDirty && o.isDirty()) {
      // inline "discard?" — never a blocking confirm()
      guard.innerHTML = `<div class="mrl-discard" role="alert"><span>Discard what you typed?</span><button type="button" class="btn btn-secondary btn-sm" data-mrl-keep>Keep editing</button><button type="button" class="btn btn-danger btn-sm" data-mrl-discard>Discard</button></div>`;
      guard.querySelector('[data-mrl-keep]')?.addEventListener('click', () => { guard.innerHTML = ''; });
      guard.querySelector('[data-mrl-discard]')?.addEventListener('click', () => close(true));
      bg.querySelector('.mrl-mbody')?.scrollTo({ top: 0 });
      return;
    }
    closed = true;
    closeKebab();
    document.removeEventListener('keydown', onKey);
    bg.remove();
    o.onClose?.();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || !bg.isConnected) return;
    if (document.querySelector('[data-kebab-pop]')) return;   // the ⋯ menu closes first
    e.preventDefault();
    close();
  };
  bg.addEventListener('click', (e) => { if (e.target === bg || (e.target as HTMLElement).closest('[data-mrl-close]')) close(); });
  document.addEventListener('keydown', onKey);
  return { body: bg.querySelector<HTMLElement>('[data-mrl-sheet-body]')!, close, root: bg };
}

/** The MTR page's modal around the same panel. */
export function openRentLedgerModal(opts: RentLedgerOpts & { title?: string }): { close: () => void; redraw: () => void; isDirty: () => boolean } {
  const t = opts.getTenant();
  let api: { redraw: () => void; isDirty: () => boolean } | null = null;
  const sheet = openMtrSheet({
    title: opts.title || `${t?.name || 'Tenant'}`,
    sub: `Rent & notes${t?.listing_name ? ` · ${t.listing_name}` : ''}`,
    avatar: initials(t?.name),
    label: 'Rent & notes',
    isDirty: () => !!(api && api.isDirty()),
    onClose: opts.onClose,
  });
  api = mountRentLedger(sheet.body, opts);
  return { close: () => sheet.close(true), redraw: () => api && api.redraw(), isDirty: () => !!(api && api.isDirty()) };
}
